import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Alert, Button, Flex, Form, Spin } from "antd"
import { useStripe } from "@stripe/react-stripe-js"
import { useMediaQuery } from "react-responsive"
import { routes } from "router"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import { isFeatureEnabled } from "config/features"
import { checkStripeFullyConnected } from "store/user"
import { validateWidgetDiscount } from "store/discounts"
import { isPromoCheckRateLimited } from "utils/discountUtils"
import {
  changeWidgetCartItem,
  clearWidgetCart,
  releaseWidgetCart,
  removeFromWidgetCart,
  resetBookingInfo,
  resetBookingSelections,
  setWidgetCartPaying,
  widgetCartItemsSelector,
  widgetCartTokenSelector
} from "store/experiences"
import { CART_HOLD_STATUSES, ORDER_ERRORS_FOR_ANOTHER_TIME } from "../constants"
import { hasCartItem } from "../bookingDescription"
import { getBookableTicketTypes, getBookingId } from "../helpers"
import UnavailableExperience from "../UnavailableExperience"
import useCartExperiences, { EXPERIENCE_FAILED } from "../useCartExperiences"
import CartHoldTimer from "../CartHold/CartHoldTimer"
import ReservationExpiredNotice from "../CartHold/ReservationExpiredNotice"
import useCartHold from "../CartHold/useCartHold"
import ContactSection from "./ContactSection"
import PaymentSection from "./PaymentSection"
import CheckoutSummary from "./CheckoutSummary"
import BookingSuccess from "./BookingSuccess"
import useWidgetCheckout from "./useWidgetCheckout"
import useWidgetOrderCheckout from "./useWidgetOrderCheckout"
import { forgetCheckoutDetails, readCheckoutDetails, saveCheckoutDetails } from "./checkoutDetailsStorage"
import {
  canPayDeposit as getCanPayDeposit,
  combineCheckoutAmounts,
  getAmountsChargedToday,
  getOrderBookingAmounts,
  getOrderBookingPromos,
  isWithinApprovalWindow,
  PAYMENT_METHODS,
  toDiscountPromo
} from "./payment"
import styles from "./WidgetCheckout.module.css"

const CONTACT_FIELDS = ["first_name", "last_name", "email", "phone_number", "accessibility_enabled", "accessibility_notes"]
const CARD_FIELDS = ["cardNumber", "cardName", "expiry", "cvc", "zip"]
// Items that can be paid: reserved for the guest, or already in the booking or order being paid.
const PAYABLE_HOLD_STATUSES = new Set([CART_HOLD_STATUSES.held, CART_HOLD_STATUSES.booked])

// The balance line under Confirm and Pay for the deposit options.
const LATER_TEXT_KEYS = { cash: "booking_widget.checkout.cash_later", later: "booking_widget.checkout.card_later" }

// The ID the success screen shows.
// "Reservation Confirmation": contact details, payment option and method, and a summary per booking
// with the order total on one page. Paying creates the booking and charges it (useWidgetCheckout);
// several experiences are booked and paid together as one order (useWidgetOrderCheckout).
// Tablet and phone title it "Confirmation", as their designs do.
const WidgetCheckout = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const stripe = useStripe()
  const { uuid } = useParams()
  const [form] = Form.useForm()
  const isCompact = useMediaQuery({ maxWidth: 991 })
  const cartItems = useSelector(widgetCartItemsSelector)
  const cartToken = useSelector(widgetCartTokenSelector)
  const items = useMemo(() => cartItems.filter(hasCartItem), [cartItems])
  // Several experiences are reviewed together (one contact, one payment option, one order total) and
  // paid as one order; one is booked and paid as before.
  const isSeveral = items.length > 1
  const selection = isSeveral ? null : items[0] || null
  const experiences = useCartExperiences(items)
  // What the guest filled in earlier for this cart (a reload, or back from the cart), put back once the
  // form is shown.
  const [savedDetails] = useState(() => readCheckoutDetails(uuid, cartToken))
  const isRestoredRef = useRef(false)
  // The order's promo code and the cart items it applies to.
  const [appliedPromo, setAppliedPromo] = useState(null)
  // What was paid, kept for the success screen (the cart is emptied): { orderId, bookings: [{ key,
  // booking, experience, selection }] }. Several bookings come with the API's order payment.
  const [bookedOrder, setBookedOrder] = useState(null)
  const [wallet, setWallet] = useState({ paymentRequest: null, isGooglePay: false, isApplePay: false })
  const hasItem = items.length > 0
  // "Availability has changed" after the API refused a ticketed booking: { message, isSlotGone }.
  const [availabilityIssue, setAvailabilityIssue] = useState(null)
  // An order item the API refused: { key, message, cartId }. Nothing was created or charged.
  const [orderItemIssue, setOrderItemIssue] = useState(null)
  // The order's charge today when it differed from the checkout's own figure: Pay then asks for it.
  const [confirmedChargeNow, setConfirmedChargeNow] = useState(null)
  // Nothing is paid until "Check availability & continue" reserves the cart again: the reservation ran
  // out or was released, an item isn't reserved, or the order went unpaid too long.
  const [isOrderExpired, setIsOrderExpired] = useState(false)
  const { isReservationOver } = useCartHold()
  const isCartReserved = items.every((item) => PAYABLE_HOLD_STATUSES.has(item.holdStatus))
  const isPayBlocked = isOrderExpired || isReservationOver || !isCartReserved
  // A booking's experience could not be loaded (removed, unpublished): say so instead of loading forever.
  const hasFailed = items.some((item) => experiences[item.experienceId] === EXPERIENCE_FAILED)
  const isLoaded = hasItem && !hasFailed && items.every((item) => experiences[item.experienceId])
  const paymentOption = Form.useWatch("paymentOption", form) || "full"
  const paymentMethod = Form.useWatch("paymentMethod", form) || PAYMENT_METHODS.card
  // By card and wallets a request is charged when the guide accepts it; by PayPal everything now.
  const isChargedOnApproval = paymentMethod !== PAYMENT_METHODS.paypal

  const promosFor = useCallback((item) => (item && appliedPromo?.cartIds.includes(item.cartId) ? [appliedPromo] : []), [appliedPromo])

  // Each booking with its experience and amounts for the chosen payment option. In an order, one that
  // starts within 24 hours is a request the guide accepts or declines (isRequest).
  const bookings = useMemo(() => {
    if (!isLoaded) return null
    const listed = items.map((item) => ({
      key: item.cartId,
      experience: experiences[item.experienceId],
      selection: item,
      isRequest: items.length > 1 && isWithinApprovalWindow({ selection: item })
    }))
    const promosList = getOrderBookingPromos(listed, appliedPromo)
    const priced = listed.map((booking, index) => ({ ...booking, promos: promosList[index] }))
    const amountsList = getOrderBookingAmounts(priced, paymentOption)
    return priced.map(({ key, experience, selection: item, isRequest }, index) => ({ key, experience, selection: item, isRequest, amounts: amountsList[index] }))
  }, [isLoaded, items, experiences, appliedPromo, paymentOption])
  const amounts = useMemo(() => {
    if (!bookings) return null
    return isSeveral ? combineCheckoutAmounts(bookings.map((booking) => booking.amounts)) : bookings[0].amounts
  }, [bookings, isSeveral])
  // What is charged today: by card without the requests ({ approvalCharge } is theirs, charged when
  // the guide accepts), by PayPal the whole order. The wallets charge as a card does.
  const cardAmounts = useMemo(() => (amounts ? getAmountsChargedToday(amounts, bookings, true) : null), [amounts, bookings])
  const todayAmounts = isChargedOnApproval ? cardAmounts : amounts
  const experience = !isSeveral && bookings ? bookings[0].experience : null
  // Every experience in the widget is the same guide's.
  const guide = bookings?.[0]?.experience?.guide
  const isStripeConnected = checkStripeFullyConnected(guide?.stripe_account_data)
  const isPayPalAvailable = !!guide?.paypal_account_data?.is_connected && isFeatureEnabled("PAYPAL_SUPPORT")
  // With several experiences the deposit options are offered only when every one of them allows them.
  const canPayDeposit = !!bookings?.length && bookings.every((booking) => getCanPayDeposit({ experience: booking.experience, selection: booking.selection }))

  const handleBooked = (booking) => {
    forgetCheckoutDetails(uuid)
    setBookedOrder({ orderId: null, bookings: [{ key: selection.cartId, booking: { ...booking, bookingId: getBookingId(booking) }, experience, selection }] })
    dispatch(clearWidgetCart())
    dispatch(resetBookingSelections())
  }

  // The order is paid: a row per booking (its details from the API), and the cart is done with.
  const handleOrderPaid = (order) => {
    const booked = (order.bookings || []).map((orderBooking) => {
      const item = items.find((cartItem) => cartItem.cartId === orderBooking.client_item_id)
      return {
        key: orderBooking.client_item_id,
        booking: { ...orderBooking.details, bookingId: orderBooking.booking_id ? String(orderBooking.booking_id) : getBookingId(orderBooking.details) },
        experience: experiences[orderBooking.experience_id] || experiences[item?.experienceId],
        selection: item
      }
    })
    forgetCheckoutDetails(uuid)
    setBookedOrder({ orderId: order.order_number || null, bookings: booked.filter((row) => row.experience && row.selection) })
    dispatch(clearWidgetCart())
    dispatch(resetBookingSelections())
  }

  // After "Check availability & continue": items whose places are gone are shown in the cart to change
  // or remove.
  const handleAvailabilityChecked = (isAllHeld) => {
    setIsOrderExpired(false)
    if (!isAllHeld) navigate(routes.widgetCart({ uuid }))
  }

  // A ticket type with fewer places left is lowered to them in the cart (the total follows) and the
  // guest checks and pays again; a start time that is gone sends the guest back to choose another.
  const handleUnavailable = (unavailable) => {
    const ticketType = getBookableTicketTypes(experience).find((type) => String(type.id) === String(unavailable.ticketTypeId))
    if (unavailable.kind === "tickets" && ticketType) {
      const current = Number(selection.ticketCounts?.[ticketType.id] || 0)
      const ticketCounts = { ...selection.ticketCounts, [ticketType.id]: Math.min(current, unavailable.remaining) }
      const totalGuests = Object.values(ticketCounts).reduce((sum, count) => sum + Number(count || 0), 0)
      if (totalGuests > 0) {
        dispatch(changeWidgetCartItem(selection.cartId, { ticketCounts, totalGuests }))
        setAvailabilityIssue({
          message: t("booking_widget.experience.availability.type_places_now", { count: unavailable.remaining, title: ticketType.title }),
          isSlotGone: false
        })
        return
      }
    }
    setAvailabilityIssue({ message: t("booking_widget.experience.availability.slot_unavailable"), isSlotGone: true })
  }

  const chooseAnotherTime = () => {
    dispatch(removeFromWidgetCart(selection.cartId))
    navigate(routes.widgetExperience({ uuid, id: selection.experienceId }))
  }

  const singleCheckout = useWidgetCheckout({ experience, selection, promos: promosFor(selection), onBooked: handleBooked, onUnavailable: handleUnavailable })
  const orderCheckout = useWidgetOrderCheckout({
    guideUuid: uuid,
    guideId: bookings?.[0]?.experience?.guide?.id,
    cartToken,
    items,
    isEnabled: isSeveral && isLoaded,
    onPaid: handleOrderPaid,
    onExpired: () => setIsOrderExpired(true),
    onItemError: setOrderItemIssue,
    onPriceChanged: setConfirmedChargeNow
  })
  const checkout = isSeveral ? orderCheckout : singleCheckout
  const { resetPaymentIntent } = singleCheckout

  // The cart's timer and its "still shopping?" popup are hidden while a payment runs.
  useEffect(() => {
    dispatch(setWidgetCartPaying(checkout.isPaying))
  }, [checkout.isPaying, dispatch])
  useEffect(() => () => dispatch(setWidgetCartPaying(false)), [dispatch])

  // A new checkout must not reuse the previous booking session's guest token (as the old widget).
  useEffect(() => {
    localStorage.removeItem("guest_token")
    return () => dispatch(resetBookingInfo())
  }, [dispatch])

  // A different payment option or promo changes what is charged, so an intent made earlier is stale,
  // and an order's charge the guest was asked to confirm no longer applies.
  useEffect(() => {
    resetPaymentIntent()
    setConfirmedChargeNow(null)
  }, [paymentOption, paymentMethod, appliedPromo, items.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // One code for the order, checked against each booking, and applied to every booking it is valid
  // for (getOrderBookingPromos: a percentage on each, an amount off split between them). A check
  // refused for too many tries says nothing about the code, so the guest is asked to try again.
  const validatePromo = async (code) => {
    const clientIdentifier = form.getFieldValue("email") || form.getFieldValue("phone_number")?.fullNumber
    const results = await Promise.allSettled(
      bookings.map((booking) => dispatch(validateWidgetDiscount(code, booking.amounts.guestSubtotal, booking.experience.uuid, clientIdentifier)))
    )
    const rateLimited = results.find((result) => result.status === "rejected" && isPromoCheckRateLimited(result.reason))
    if (rateLimited) throw rateLimited.reason
    const valid = results.map((result, index) => ({ result, key: bookings[index].key })).filter(({ result }) => result.status === "fulfilled")
    if (!valid.length) throw results[0].reason
    const promo = toDiscountPromo(valid[0].result.value, code)
    const validKeys = valid.map(({ key }) => key)
    setAppliedPromo({ ...promo, cartIds: validKeys })
    saveCheckoutDetails(uuid, cartToken, { promoCode: promo.code })
  }

  const removePromo = () => {
    setAppliedPromo(null)
    saveCheckoutDetails(uuid, cartToken, { promoCode: null })
  }

  // The saved details go back into the form once it is shown, and the saved promo code is checked
  // again for the cart as it is now (dropped when no longer valid). A wallet is not chosen again: the
  // browser may not offer it this time.
  useEffect(() => {
    if (!bookings || isRestoredRef.current) return
    isRestoredRef.current = true
    if (!savedDetails) return
    const { paymentMethod: savedMethod, ...values } = savedDetails.values
    const isMethodAvailable = savedMethod === PAYMENT_METHODS.card || (savedMethod === PAYMENT_METHODS.paypal && isPayPalAvailable)
    form.setFieldsValue({ ...values, ...(isMethodAvailable && { paymentMethod: savedMethod }) })
    if (savedDetails.promoCode) {
      validatePromo(savedDetails.promoCode).catch((error) => {
        if (!isPromoCheckRateLimited(error)) saveCheckoutDetails(uuid, cartToken, { promoCode: null })
      })
    }
  }, [bookings]) // eslint-disable-line react-hooks/exhaustive-deps

  // Only the total today can be paid by wallet; fall back to the card when a deposit is chosen.
  useEffect(() => {
    if (paymentOption !== "full" && [PAYMENT_METHODS.googlePay, PAYMENT_METHODS.applePay].includes(paymentMethod)) {
      form.setFieldValue("paymentMethod", PAYMENT_METHODS.card)
    }
    if (!canPayDeposit && paymentOption !== "full") form.setFieldValue("paymentOption", "full")
  }, [paymentOption, paymentMethod, canPayDeposit, form])

  // Apple / Google Pay through Stripe's Payment Request, offered only when the browser has a wallet.
  const latestPayRef = useRef(null)
  // What Pay charges today: the checkout's own figure, or the order's when the guest was asked to
  // confirm it.
  const chargeNow = confirmedChargeNow ?? todayAmounts?.chargeNow ?? 0
  const payDetails = () => ({ values: form.getFieldsValue(true), paymentOption, promoCode: appliedPromo?.code, expectedChargeNow: chargeNow })
  latestPayRef.current = payDetails
  const chargeNowCents = cardAmounts ? Math.round((confirmedChargeNow ?? cardAmounts.chargeNowByOption.full) * 100) : 0
  useEffect(() => {
    if (!stripe || !isStripeConnected || !chargeNowCents || wallet.paymentRequest) return
    const paymentRequest = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: { label: experience?.title || "Total", amount: chargeNowCents },
      requestPayerName: true,
      requestPayerEmail: true,
      disableWallets: ["link"]
    })
    paymentRequest.canMakePayment().then((result) => {
      if (result) setWallet({ paymentRequest, isGooglePay: !!result.googlePay, isApplePay: !!result.applePay })
    })
  }, [stripe, isStripeConnected, chargeNowCents, wallet.paymentRequest, experience?.title])

  useEffect(() => {
    if (wallet.paymentRequest && chargeNowCents) wallet.paymentRequest.update({ total: { label: experience?.title || "Total", amount: chargeNowCents } })
  }, [wallet.paymentRequest, chargeNowCents, experience?.title])

  // Registered once per payment request; reads the latest pay function and form values through refs.
  const payByWalletRef = useRef(checkout.payByWallet)
  payByWalletRef.current = checkout.payByWallet
  useEffect(() => {
    const { paymentRequest } = wallet
    if (!paymentRequest) return undefined
    const handlePaymentMethod = (event) => payByWalletRef.current(event, latestPayRef.current())
    paymentRequest.on("paymentmethod", handlePaymentMethod)
    return () => paymentRequest.off("paymentmethod", handlePaymentMethod)
  }, [wallet])

  const validateContact = async () => {
    try {
      await form.validateFields(CONTACT_FIELDS)
      return true
    } catch (error) {
      form.scrollToField(error?.errorFields?.[0]?.name, { behavior: "smooth", block: "center" })
      return false
    }
  }

  const clearIssues = () => {
    setAvailabilityIssue(null)
    setOrderItemIssue(null)
  }

  const handleConfirm = async () => {
    clearIssues()
    try {
      await form.validateFields([...CONTACT_FIELDS, ...CARD_FIELDS])
    } catch (error) {
      form.scrollToField(error?.errorFields?.[0]?.name, { behavior: "smooth", block: "center" })
      return
    }
    checkout.payByCard(payDetails())
  }

  const walletProps = {
    ...wallet,
    onClick: (event) => {
      // The wallet sheet must open from the click itself, so block it right away and only when the
      // contact details are already known to be incomplete.
      const errors = form.getFieldsError(CONTACT_FIELDS).filter(({ errors: fieldErrors }) => fieldErrors.length)
      const missing = CONTACT_FIELDS.slice(0, 4).some((field) => !form.getFieldValue(field))
      if (isPayBlocked) {
        event.preventDefault()
        return
      }
      if (errors.length || missing) {
        event.preventDefault()
        validateContact()
        return
      }
      clearIssues()
    }
  }

  const paypalProps = {
    onClick: async (_, actions) => {
      if (isPayBlocked || !(await validateContact())) return actions.reject()
      clearIssues()
      return actions.resolve()
    },
    createOrder: () => checkout.createPayPalOrderForBooking(payDetails()),
    onApprove: (data) => checkout.approvePayPalOrder(data?.orderID),
    onError: checkout.handlePayPalError
  }

  const goToListing = () => navigate(routes.widgetListing({ uuid }), { replace: true })

  const chooseAnotherTimeFor = (cartId) => {
    const item = items.find((cartItem) => cartItem.cartId === cartId)
    dispatch(removeFromWidgetCart(cartId))
    navigate(item ? routes.widgetExperience({ uuid, id: item.experienceId }) : routes.widgetCart({ uuid }))
  }

  if (bookedOrder) return <BookingSuccess order={bookedOrder} onClose={goToListing} />

  const header = (
    <Flex align="center" justify="space-between" gap={16} wrap="wrap">
      <Flex align="center" gap={20}>
        <Button
          shape="circle"
          className={styles.backButton}
          aria-label={t("booking_widget.checkout.back")}
          onClick={() => navigate(routes.widgetCart({ uuid }))}
          icon={<Icon type="feather" name="ChevronLeft" width={20} height={20} color="currentColor" />}
        />
        <h1 className={styles.title}>{t(isCompact ? "booking_widget.checkout.title_short" : "booking_widget.checkout.title")}</h1>
      </Flex>
      <CartHoldTimer />
    </Flex>
  )

  if (!hasItem) return <Navigate to={routes.widgetCart({ uuid })} replace />

  // With several, the cart shows which one is gone and lets the guest remove it.
  if (hasFailed && isSeveral) return <Navigate to={routes.widgetCart({ uuid })} replace />

  if (hasFailed) {
    return (
      <UnavailableExperience
        onChooseExperience={() => {
          dispatch(releaseWidgetCart())
          goToListing()
        }}
      />
    )
  }

  if (!bookings || !amounts) {
    return (
      <Flex justify="center" className="p-xl">
        <Spin />
      </Flex>
    )
  }

  const laterTextKey = LATER_TEXT_KEYS[paymentOption]
  const laterText = laterTextKey && t(laterTextKey, { amount: numberToCurrency(amounts.balance) })

  return (
    <Spin spinning={checkout.isPaying}>
      <Flex vertical gap={40}>
        {header}
        <div className={styles.layout}>
          <Form
            form={form}
            layout="vertical"
            requiredMark
            className={styles.form}
            onValuesChange={(_, allValues) => saveCheckoutDetails(uuid, cartToken, { values: allValues })}
          >
            <Flex vertical gap={12}>
              <ContactSection form={form} initialPhone={savedDetails?.values.phone_number} />
              <PaymentSection
                form={form}
                amounts={todayAmounts}
                canPayDeposit={canPayDeposit}
                paymentOption={paymentOption}
                paymentMethod={paymentMethod}
                wallet={walletProps}
                isPayPalAvailable={isPayPalAvailable}
                paypal={paypalProps}
                isPaying={checkout.isPaying}
              />
            </Flex>
            {checkout.paymentError && <Alert type="error" showIcon message={checkout.paymentError} className="mt-m" />}
            {isPayBlocked && <ReservationExpiredNotice className="mt-m" onChecked={handleAvailabilityChecked} />}
            {confirmedChargeNow != null && (
              <Alert type="info" showIcon className="mt-m" message={t("booking_widget.checkout.charge_changed", { amount: numberToCurrency(confirmedChargeNow) })} />
            )}
            {orderItemIssue && (
              <Alert
                type="warning"
                showIcon
                className="mt-m"
                message={
                  <span>
                    <strong>{experiences[items.find((item) => item.cartId === orderItemIssue.cartId)?.experienceId]?.title}</strong>
                    {": "}
                    {orderItemIssue.message || t("booking_widget.checkout.errors.generic")}
                  </span>
                }
                action={
                  ORDER_ERRORS_FOR_ANOTHER_TIME.includes(orderItemIssue.key) && (
                    <Button size="small" onClick={() => chooseAnotherTimeFor(orderItemIssue.cartId)}>
                      {t("booking_widget.checkout.choose_another_time")}
                    </Button>
                  )
                }
              />
            )}
            {availabilityIssue && (
              <Alert
                type="warning"
                showIcon
                className="mt-m"
                message={
                  <span>
                    <strong>{t("booking_widget.experience.availability_changed")}</strong>: {availabilityIssue.message}
                  </span>
                }
                action={
                  availabilityIssue.isSlotGone && (
                    <Button size="small" onClick={chooseAnotherTime}>
                      {t("booking_widget.checkout.choose_another_time")}
                    </Button>
                  )
                }
              />
            )}
            <Flex vertical gap={12} className="mt-l">
              <span className={styles.agreeText}>
                {t("booking_widget.checkout.agree")}{" "}
                <a href={`#${routes.cancellationpolicy()}`} target="_blank" rel="noopener noreferrer">
                  {t("booking_widget.checkout.cancellation_policy")}
                </a>
              </span>
              {paymentMethod === PAYMENT_METHODS.card && (
                <>
                  <Button
                    type="primary"
                    shape="round"
                    size="large"
                    block
                    disabled={!isStripeConnected || checkout.isPaying || isPayBlocked}
                    onClick={handleConfirm}
                  >
                    {t("booking_widget.checkout.confirm_and_pay", { amount: numberToCurrency(chargeNow) })}
                  </Button>
                  {laterText && <span className={styles.laterText}>{laterText}</span>}
                  {todayAmounts.approvalCharge > 0 && (
                    <span className={styles.laterText}>{t("booking_widget.checkout.approval_later", { amount: numberToCurrency(todayAmounts.approvalCharge) })}</span>
                  )}
                </>
              )}
            </Flex>
          </Form>
          <aside className={styles.summaryColumn}>
            <CheckoutSummary
              bookings={bookings}
              isChargedOnApproval={isChargedOnApproval}
              orderAmounts={amounts}
              appliedPromo={appliedPromo}
              onValidatePromo={validatePromo}
              onRemovePromo={removePromo}
            />
          </aside>
        </div>
      </Flex>
    </Spin>
  )
}

export default WidgetCheckout
